'use client';

import { useState, useCallback, useMemo } from 'react';

interface UseUnsavedChangesOptions<T> {
  initialData: T;
  currentData: T;
  isEqual?: (a: T, b: T) => boolean;
}

/**
 * Simple hook to track if user has interacted with a form
 * Once user does anything, always show warning on close (even if they delete everything)
 * @returns Object with hasUserInteracted boolean and markAsInteracted function
 */
export function useUserInteraction() {
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  const markAsInteracted = useCallback(() => {
    setHasUserInteracted(true);
  }, []);

  const reset = useCallback(() => {
    setHasUserInteracted(false);
  }, []);

  return {
    hasUnsavedChanges: hasUserInteracted, // Alias for consistency
    hasUserInteracted,
    markAsInteracted,
    reset,
  };
}

/**
 * Custom hook to track unsaved changes in forms (for edit dialogs)
 * @param initialData - The original/initial data
 * @param currentData - The current form data
 * @param isEqual - Optional custom equality function
 * @returns Object with hasUnsavedChanges boolean and reset function
 */
export function useUnsavedChanges<T>({
  initialData,
  currentData,
  isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b),
}: UseUnsavedChangesOptions<T>) {
  const [resetSnapshotKey, setResetSnapshotKey] = useState<string | null>(null);
  const comparisonSnapshotKey = useMemo(() => JSON.stringify([initialData, currentData]), [initialData, currentData]);

  // Compare current form data to the original data unless explicitly reset for this exact snapshot.
  const hasUnsavedChanges = useMemo(() => {
    if (resetSnapshotKey === comparisonSnapshotKey) {
      return false;
    }
    return !isEqual(initialData, currentData);
  }, [resetSnapshotKey, comparisonSnapshotKey, initialData, currentData, isEqual]);

  const reset = useCallback(() => {
    // A reset acknowledges the current snapshot as clean until inputs change again.
    setResetSnapshotKey(comparisonSnapshotKey);
  }, [comparisonSnapshotKey]);

  return {
    hasUnsavedChanges,
    reset,
  };
}

/**
 * Helper function to create a deep equality checker for objects with arrays
 * Useful for forms with multiselect fields
 */
export function createDeepEqualityChecker<T>() {
  return (a: T, b: T): boolean => {
    return JSON.stringify(a) === JSON.stringify(b);
  };
}

/**
 * Helper function to create a shallow equality checker
 * Useful for simple forms without nested objects
 */
export function createShallowEqualityChecker<T>() {
  return (a: T, b: T): boolean => {
    if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
      return a === b;
    }

    const keysA = Object.keys(a as object);
    const keysB = Object.keys(b as object);

    if (keysA.length !== keysB.length) {
      return false;
    }

    for (const key of keysA) {
      if ((a as any)[key] !== (b as any)[key]) {
        return false;
      }
    }

    return true;
  };
}
